/* eslint-disable camelcase */
/**
 *  Widgets - Highest scoring half basketball
 *  @module Widgets
 *  @author Livescore <info@score-stats.com>
 *  @copyright 2022 livescore
 */

import { MatchService } from '@services/match.service';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BaseComponent } from '@widgets/pages/base.component';
import { Observable, Subscription, throwError } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap, tap } from 'rxjs/operators';

import { DemoService } from '@widgets/services/demo.service';
import { WidgetMixin } from '@widgets/services/mixins/widget.mixin';

import { Form, LastForm } from '@widgets/interfaces/widget.interface';
import CONFIG from '@widgets/config/widget.config';


import { ClientService } from '@/services/client.service';
import { MatchData, Team } from '@/interfaces/match-data.interface';
import { LangService } from '@/services/lang.service';
import { MatchType } from '@/interfaces/league-table.interface';


@Component({
    selector: 'widgets-highest-scoring-half',
    templateUrl: './highest-scoring-half.component.html',
    styleUrls: ['../styles.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class HighestScoringHalfComponent extends WidgetMixin(BaseComponent) implements OnInit, OnDestroy {
    public matches: MatchData[] = [];

    private matchId: number;

    private subscriber: Subscription;

    public data: Form | null = null;

    public sportName: string;

    public periods: [string, [string, string]][] = [
        ['1_half', ['score_period_1', 'score_period_2']],
        ['2_half', ['score_period_3', 'score_period_4']],
    ];

    public constructor(
        private router: ActivatedRoute,
        private match: MatchService,
        private lang: LangService,
        private demo: DemoService,
        public client: ClientService,
    ) {
        super(demo);
    }

    /**
     * Load data from server
     */
    public ngOnInit(): void {
        super.ngOnInit();

        this.setTitle(this.router);

        this.router.data.subscribe((sub) => {
            this.num = sub.num;

            if (sub.br && sub.br.length > 0) {
                [this.matchId] = sub.br;
            } else {
                this.finished();
            }

            if (this.matchId) {
                this.load();
            } else {
                this.error();
            }
        });
    }

    public ngOnDestroy(): void {
        if (this.subscriber instanceof Subscription) {
            this.subscriber.unsubscribe();
        }

        this.matches.splice(0);
    }


    public periodsAvg(side: 'home' | 'away', period: string): number {
        let total = 0;
        let count = 0;

        const reversed = this.data![side].form.length > this.num
            ? this.data![side].form.slice(1) : this.data![side].form;

        const periodVal = this.findPeriod(period);

        reversed.forEach((f) => {
            if (f.code_state !== 'not_started' && periodVal && f[periodVal[0]] && f[periodVal[1]]) {
                const [home1, away1] = (f[periodVal[0]] as string).split(':');
                const [home2, away2] = (f[periodVal[1]] as string).split(':');


                const home = +home1 + +home2;
                const away = +away1 + +away2;
                const isHome = this.data![side].name === f.home_team;


                count += 1;
                if (isHome) {
                    total += +home;
                } else {
                    total += +away;
                }
            }
        });

        return (total / count);
    }


    public total(period: string): number {
        return this.periodsAvg('home', period) + this.periodsAvg('away', period);
    }

    public width(side: 'home' | 'away', period: string): number {
        return Math.round((this.periodsAvg(side, period) / this.total(period)) * 100);
    }

    private findPeriod(period: string): [string, string] | null {
        const periodVal = this.periods.find(p => p[0] === period);

        return periodVal![1] ?? null;
    }

    private load(): void {
        const iso: string | null = this.lang.getLangSnapshot();
        let homeId: number | null = null;
        let awayId: number | null = null;

        this.subscriber = this.match
            .getMatchNew(this.matchId, iso as string, true)
            .pipe(
                tap((match: MatchData) => {
                    this.sportName = match.sport_code_name;
                }),
                filter((match: MatchData) => CONFIG.highestScoringHalf.availSports.includes(match.sport_code_name)),
                mergeMap(
                    (match: MatchData): Observable<Form> => {
                        const [p1, p2] = match.participants.map((p: Team): number => p.id);
                        homeId = p1;
                        awayId = p2;
                        return this.demo.form(p1, p2, iso, { limit: this.num });
                    },
                ),
                map((data: Form) => {
                    data.home = { ...data.home, id: homeId };
                    data.away = { ...data.away, id: awayId };

                    data.home.form = data.home.form.filter((f: LastForm) => f.type !== MatchType.UPCOMING);
                    data.away.form = data.away.form.filter((f: LastForm) => f.type !== MatchType.UPCOMING);
                    return data;
                }),
                switchMap(this.checkEmpty()),
                catchError(
                    (e): Observable<any> => {
                        this.error(e);
                        return throwError(e);
                    },
                ),
            )

            .subscribe(
                (data) => {
                    this.data = data;
                    this.finished();
                },
                () => {},
                () => {
                    this.loaded = true;
                },
            );
    }
}
